// react
import { Suspense, useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// api
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';
import { useGetEnergyGroupQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import OrgChartComponent from '@/features/energyxplorer/components/tree/OrgChart';
import TreeGraphFilterInput from '@/features/energyxplorer/components/tree/TreeGraphFilterInput';

// feature-hooks
import useUrlFilters from '@/features/energyxplorer/hooks/useUrlFilters';

// components
import { useUrlDateRange } from '@/hooks/useUrlDateRange';

// relative
import { useGetEnergyDataQuery } from '../../../../api/energy-analysis/energyDataApiSlice';

const EnergyTreeGraphTab = () => {
  const { t } = useTranslation('energyxplorer');
  const { accountId, energyGroupId } = useParams({ strict: false });
  const { timeRange } = useUrlDateRange();
  const { data: selectedEnergyGroup } = useGetEnergyGroupQuery(
    { groupId: energyGroupId },
    { skip: !energyGroupId },
  );

  const { filters, setFilters } = useUrlFilters({
    activePeriodFilter: undefined,
    activeHolidaysFilter: undefined,
  });

  // Dialog states moved from OrgChart
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const { data: units, isLoading: isLoadingUnits } =
    useUnitsGetCollectionAsyncByqueryOptionsQuery(
      { 'Account.AccountId': `eq(${accountId})` },
      {
        skip: !accountId,
      },
    );

  const queryArgs = useMemo(() => {
    return {
      accountId: accountId,
      energyGroupId: selectedEnergyGroup?.energyGroupId,
      start: timeRange?.start,
      end: timeRange?.end,
      ...filters,
    };
  }, [accountId, selectedEnergyGroup, timeRange, filters]);

  const {
    data: energyData,
    isLoading: isLoadingEnergyData,
    isFetching: isFetchingEnergyData,
  } = useGetEnergyDataQuery(queryArgs, {
    skip:
      !selectedEnergyGroup?.energyGroupId ||
      !queryArgs.energyGroupId ||
      !selectedEnergyGroup?.energyTreeNodes ||
      selectedEnergyGroup?.energyTreeNodes?.length > 250 ||
      !queryArgs.accountId,
  });

  const enhancedEnergyGroup = useMemo(() => {
    if (!selectedEnergyGroup || !energyData?.NodeEnergySummaries)
      return selectedEnergyGroup;

    const summaryMap = energyData?.NodeEnergySummaries.reduce(
      (acc, summary) => {
        acc[summary.Id] = summary;
        return acc;
      },
      {},
    );

    const enhancedNodes = selectedEnergyGroup.energyTreeNodes.map((node) => ({
      ...node,
      energySummary: summaryMap[node.energyTreeNodeId] || null,
    }));

    return {
      ...selectedEnergyGroup,
      energyTreeNodes: enhancedNodes,
    };
  }, [selectedEnergyGroup, energyData?.NodeEnergySummaries]);

  return (
    <>
      <Suspense fallback={<div>{t('analysis.loading')}</div>}>
        <GatewayLoadingIndicator
          isLoading={
            isLoadingEnergyData || isFetchingEnergyData || isLoadingUnits
          }
        />
      </Suspense>

      <OrgChartComponent
        data={enhancedEnergyGroup?.energyTreeNodes || []}
        units={units}
        onOpenFilterDialog={() => setFilterDialogOpen(true)}
      />

      <TreeGraphFilterInput
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        viewMode="graph"
        context="energyTree"
        handleApply={(newFilters) => {
          setFilters(newFilters); // Should include keys: active_period_filter, active_days_filter, holidays_filter
        }}
        selectedEnergyGroup={selectedEnergyGroup}
      />
    </>
  );
};

export default EnergyTreeGraphTab;
